import { z } from 'zod';
import { objectId } from './schemas.js';
/**
 * The customer side of the company: who buys from it, who to ring, and what
 * is currently being sold to them.
 *
 * A workspace already knows everything about its own people and nothing about
 * the people who pay for the work. This module is the other half — and it is
 * deliberately the *same* customer record the invoices are raised against.
 * Sales pipelines and receivables are usually two products that disagree about
 * who the customer is; here a won deal names the account an invoice is issued
 * to, so the question "did that deal ever get paid" is a lookup rather than a
 * reconciliation.
 *
 * Amounts are integer minor units of the workspace currency, as everywhere
 * else in the books. See `money.ts`.
 */
/* ------------------------------ customers ------------------------------- */
/**
 * Where a company stands with us, not where a deal stands.
 *
 * `lead` and `active` are the two that matter day to day. `dormant` exists so
 * a customer who has stopped buying can leave the working lists without being
 * archived — the difference between "not this quarter" and "do not contact",
 * which a single archived flag cannot express.
 */
export const CUSTOMER_STATUSES = ['lead', 'active', 'dormant', 'archived'];
export const createCustomerSchema = z.object({
    name: z.string().trim().min(2).max(120),
    industry: z.string().trim().max(80).optional(),
    website: z.string().trim().max(200).optional(),
    email: z.string().trim().email().max(200).optional().or(z.literal('')),
    phone: z.string().trim().max(40).optional(),
    address: z.string().trim().max(300).optional(),
    taxId: z.string().trim().max(60).optional(),
    status: z.enum(CUSTOMER_STATUSES).default('lead'),
    ownerId: objectId.nullable().optional(),
    note: z.string().trim().max(2000).optional(),
});
export const updateCustomerSchema = createCustomerSchema.partial();
export const customerQuerySchema = z.object({
    status: z.enum(CUSTOMER_STATUSES).optional(),
    ownerId: objectId.optional(),
    q: z.string().trim().max(80).optional(),
});
export const createContactSchema = z.object({
    customerId: objectId,
    name: z.string().trim().min(2).max(120),
    title: z.string().trim().max(120).optional(),
    email: z.string().trim().email().max(200).optional().or(z.literal('')),
    phone: z.string().trim().max(40).optional(),
    primary: z.boolean().default(false),
    note: z.string().trim().max(1000).optional(),
});
/** The customer a contact belongs to is not something a patch may move: it
    would silently transplant a person's history onto another account. */
export const updateContactSchema = createContactSchema.omit({ customerId: true }).partial();
/* -------------------------------- deals --------------------------------- */
/**
 * The pipeline, in the order it is walked.
 *
 * Ordered rather than a set, because every board, forecast and "how far has
 * this got" reading depends on one stage being further along than another.
 * `won` and `lost` are stages rather than a separate closed flag so a deal has
 * exactly one position at all times, and the board has exactly one column to
 * drop it into.
 */
export const DEAL_STAGES = [
    'lead',
    'qualified',
    'proposal',
    'negotiation',
    'won',
    'lost',
];
export const OPEN_DEAL_STAGES = ['lead', 'qualified', 'proposal', 'negotiation'];
export const isOpenStage = (stage) => OPEN_DEAL_STAGES.includes(stage);
/**
 * The default odds of each open stage, used to fill the probability box when a
 * deal is moved. They are a starting point a salesperson overrides, not a
 * model: a forecast built from unedited defaults is just a stage count wearing
 * a percentage.
 */
export const STAGE_PROBABILITY = {
    lead: 10,
    qualified: 30,
    proposal: 50,
    negotiation: 70,
    won: 100,
    lost: 0,
};
export const createDealSchema = z.object({
    customerId: objectId,
    title: z.string().trim().min(2).max(140),
    amount: z.number().int().nonnegative().max(1_000_000_000_000),
    stage: z.enum(DEAL_STAGES).default('lead'),
    probability: z.number().int().min(0).max(100).optional(),
    ownerId: objectId.optional(),
    expectedCloseDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-08-16')
        .optional(),
    source: z.string().trim().max(80).optional(),
    note: z.string().trim().max(2000).optional(),
});
export const updateDealSchema = createDealSchema
    .omit({ customerId: true })
    .partial()
    .extend({
    /** Required by the server when the patch moves the deal to `lost`. */
    lostReason: z.string().trim().max(300).optional(),
});
export const dealQuerySchema = z.object({
    stage: z.enum(DEAL_STAGES).optional(),
    customerId: objectId.optional(),
    ownerId: objectId.optional(),
    /** `open` is the working view: everything not yet won or lost. */
    view: z.enum(['open', 'all']).default('open'),
});
/* ------------------------------ activities ------------------------------ */
export const DEAL_ACTIVITY_KINDS = ['note', 'call', 'email', 'meeting'];
export const createActivitySchema = z.object({
    kind: z.enum(DEAL_ACTIVITY_KINDS).default('note'),
    body: z.string().trim().min(1).max(2000),
    occurredAt: z.string().datetime().optional(),
});
